/**
 * Cell ↔ master-field merge map (B1). "양식 변경 시 merge_map만 수정" (CONTRACT §4).
 *
 * This is the typed, code-side companion to `DocumentTemplate.merge_map` (jsonb).
 * The merge engine reads ONLY this map for coordinates — no coordinates are
 * hard-coded in the merge loop. When the xlsx layout changes, edit here.
 *
 * Coordinates below are the ACTUAL cells of the real BRITZMEDI form
 * (`templates/_source/2604_Invoice_NEWCHAE_Mexico.xlsx`), read directly from
 * `xl/worksheets/sheet2.xml` (INVOICE-US) and `sheet3.xml` (PACKING LIST). The
 * blank template `templates/ci-pl-template.xlsx` keeps the same layout with the
 * customer-specific values cleared.
 *
 * Sheet ↔ part mapping (xl/workbook.xml): sheet1 = INVOICE-CNY (data master,
 * hidden in output), sheet2 = INVOICE-US, sheet3 = PACKING LIST. INVOICE-US /
 * PACKING LIST originally pulled some header/line cells from INVOICE-CNY via
 * cross-sheet formulas; the merge writes LITERAL values into the US/PACKING cells
 * (dropping those formulas) so each sheet is self-contained and viewers that do
 * not recalculate still show the data.
 */

import type { MergeMap } from "@/types";

/** Worksheet XML part for each sheet (1-based sheetN.xml). */
export const SHEET_PARTS = {
  invoiceCny: "xl/worksheets/sheet1.xml",
  invoiceUs: "xl/worksheets/sheet2.xml",
  packing: "xl/worksheets/sheet3.xml",
} as const;

export const SHEET_NAME_INVOICE = "INVOICE-US";
export const SHEET_NAME_PACKING = "PACKING LIST";
export const SHEET_NAME_INVOICE_CNY = "INVOICE-CNY";

/** Layout for the repeating line-item block on a sheet. */
export interface TableSpec {
  /** 1-based row of the first data row. */
  start_row: number;
  /** Last data row available in the form before the TOTAL row. */
  end_row: number;
  /** Column letter -> line-item field path (or computed token). */
  cols: Record<string, string>;
  /** Numeric columns (written as <v>, not inlineStr). */
  numeric_cols: string[];
}

/** Header (scalar) cell -> dotted model path, per sheet. */
export interface HeaderMap {
  /** cells written as inline strings. */
  text: Record<string, string>;
  /** cells written as Excel date serials (model path resolves to YYYY-MM-DD). */
  date: Record<string, string>;
}

export interface CiPlMergeMap {
  engine: "zip-xml";
  invoice: {
    part: string;
    header: HeaderMap;
    table: TableSpec;
    /** TOTAL row: { qty_cell, amount_cell } (numeric). */
    totals: { row: number; qty_cell: string; amount_cell: string };
    /** Cell to host the Korean body-grade disclaimer (invariant #5). */
    disclaimer_cell: string;
  };
  packing: {
    part: string;
    header: HeaderMap;
    table: TableSpec;
    totals: {
      row: number;
      qty_cell: string;
      net_weight_cell: string;
      gross_weight_cell: string;
    };
    disclaimer_cell: string;
  };
  /** INVOICE-CNY data-master cells to blank in the template (no customer leak). */
  cny_clear_cells: string[];
}

export const CI_PL_MERGE_MAP: CiPlMergeMap = {
  engine: "zip-xml",
  invoice: {
    part: SHEET_PARTS.invoiceUs,
    header: {
      text: {
        // Shipper / Exporter block (1).
        B4: "shipper.company_name",
        B5: "shipper.address",
        B6: "shipper.tel",
        D6: "shipper.fax",
        B8: "shipper.attn",
        // Consignee block (2) "For Account & Risk of Messrs".
        B12: "consignee.company_name",
        B13: "consignee.address",
        B14: "consignee.address2",
        B15: "consignee.tax_id",
        B18: "consignee.attn",
        // Invoice no (8).
        F4: "doc.invoice_no",
        // Final destination (5).
        D30: "doc.final_destination",
        // Remarks (11): Incoterms line.
        G26: "doc.incoterms_remark",
        // Beneficiary bank block (10) — optional, from shipper.beneficiary_bank.
        F29: "shipper.bank_name_line",
        F30: "shipper.bank_account_line",
        F34: "shipper.bank_swift_line",
      },
      date: {
        // DATE: (cell I4) — Excel serial.
        I4: "doc.date",
      },
    },
    table: {
      start_row: 40,
      end_row: 50,
      cols: {
        D: "description_en",
        F: "quantity",
        H: "unit_price",
        I: "line_amount",
      },
      numeric_cols: ["F", "H", "I"],
    },
    totals: { row: 51, qty_cell: "F51", amount_cell: "I51" },
    disclaimer_cell: "A67",
  },
  packing: {
    part: SHEET_PARTS.packing,
    header: {
      text: {
        B4: "shipper.company_name",
        B5: "shipper.address",
        B6: "shipper.tel",
        D6: "shipper.fax",
        B8: "shipper.attn",
        B12: "consignee.company_name",
        B13: "consignee.address",
        B14: "consignee.address2",
        B15: "consignee.tax_id",
        B18: "consignee.attn",
        // Invoice no (8) — on PACKING this is G4.
        G4: "doc.invoice_no",
        D30: "doc.final_destination",
        G26: "doc.incoterms_remark",
      },
      date: {
        // DATE : (cell J4).
        J4: "doc.date",
      },
    },
    table: {
      start_row: 40,
      end_row: 50,
      cols: {
        D: "description_en",
        F: "quantity",
        H: "net_weight_kg",
        I: "gross_weight_kg",
        J: "cbm_text",
      },
      numeric_cols: ["F", "H", "I"],
    },
    totals: {
      row: 51,
      qty_cell: "F51",
      net_weight_cell: "H51",
      gross_weight_cell: "I51",
    },
    disclaimer_cell: "A67",
  },
  // INVOICE-CNY customer data cleared in the blank template (defence in depth:
  // even if someone unhides it, no NEWCHAE deal data remains).
  cny_clear_cells: [
    "F4", // invoice no
    "I4", // date serial
    "B13", "B14", "B15", "B16", "B19", // consignee block (CNY uses 13-19)
    "D31", // final destination
    "B41", "D41", "F41", "H41", "I41", // line 1
    "B42", "D42", "F42", "H42", "I42", // line 2
  ],
};

/** As the generic jsonb shape stored on document_templates.merge_map. */
export const CI_PL_MERGE_MAP_JSON: MergeMap =
  CI_PL_MERGE_MAP as unknown as MergeMap;
